import { lazy, Suspense, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { CoachModal } from '@/features/coach/components/CoachModal';
import { ResourceNotes } from '@/features/resource-reader/ResourceNotes';
import type { ResourcePin } from '@/lib/types';
import { useFeedbackPinRead, type FeedbackPinSource } from './useFeedbackPinRead';
const ResourcePinPreview = lazy(() => import('@/features/resource-reader/ResourcePinPreview').then(module => ({ default: module.ResourcePinPreview })));

export function FeedbackResourcePreview(props: { source: FeedbackPinSource; pin: ResourcePin; onClose(): void;
  returnFocusTo?: HTMLElement | null }) {
  return <PreviewEntry key={JSON.stringify([props.source, props.pin])} {...props} />;
}
function PreviewEntry({ source, pin, onClose, returnFocusTo }: { source: FeedbackPinSource; pin: ResourcePin;
  onClose(): void; returnFocusTo?: HTMLElement | null }) {
  const read = useFeedbackPinRead(source, pin), [lost, setLost] = useState<number | null>(null);
  const unavailable = read.unavailable || lost === read.readVersion;
  const resource = !unavailable && read.resource?.availability === 'available' ? read.resource : null;
  return <CoachModal title={resource?.display_label || (unavailable ? 'Resource unavailable' : 'Resource')}
    description="Exact pinned version" size="wide" onClose={onClose} returnFocusTo={returnFocusTo}>
    {unavailable ? <div className="space-y-3" role="alert"><p>This exact pinned Resource is unavailable. A newer version is not substituted.</p>
      {read.active && <Button variant="outline" onClick={read.refresh}>Retry Resource</Button>}</div>
      : !resource ? <p role="status">Loading pinned Resource…</p>
        : <div className="space-y-3"><Suspense fallback={<p role="status">Loading preview…</p>}><ResourcePinPreview key={read.readVersion} scope={source.scope} resource={resource}
          selection={read.selection} onUnavailable={() => setLost(read.readVersion)} /></Suspense>
          <p className="text-xs text-ink-muted">{resource.kind === 'tactical_board' ? 'Illustration · Saved tactical scene' : resource.kind === 'moment' ? 'Saved moment' : 'Saved clip'} · Exact pinned version</p>
          <ResourceNotes notes={resource.notes} />
        </div>}
  </CoachModal>;
}
